import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import SearchIcon from '@mui/icons-material/Search'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import { TABS } from '../lib/rosterFilters'

/**
 * The three controls above the table: the All / Active / Inactive tabs, the
 * search box, and the company dropdown.
 *
 * This component holds no state of its own. It shows what it is given and
 * reports what the user did — the page above owns the answers.
 */
export default function RosterFilters({
  tab,
  onTabChange,
  query,
  onQueryChange,
  company,
  companies,
  onCompanyChange,
}) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: '8px', flexWrap: 'wrap', mb: '12px' }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={tab}
        onChange={(_event, next) => {
          // MUI reports null when you click the tab that is already selected.
          // Ignoring it keeps one tab always chosen.
          if (next === null) return
          onTabChange(next)
        }}
        sx={{
          p: '2px',
          gap: '2px',
          border: '1px solid',
          borderColor: 'custom.fieldLine',
          borderRadius: '7px',
          bgcolor: 'custom.field',
          '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            m: 0,
            borderRadius: '5px',
            '&:not(:first-of-type)': { borderRadius: '5px', ml: 0 },
            '&:first-of-type': { borderRadius: '5px' },
          },
        }}
      >
        {TABS.map((label) => (
          <ToggleButton
            key={label}
            value={label}
            sx={{
              p: '4px 11px',
              fontSize: '0.76rem',
              fontWeight: 600,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              color: 'custom.ink2',
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': {
                bgcolor: 'custom.ink',
                color: 'custom.invertInk',
                '&:hover': { bgcolor: 'custom.ink' },
              },
            }}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        size="small"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search Account Id or Company"
        slotProps={{
          htmlInput: { 'aria-label': 'Search Account Id or Company' },
          input: {
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: '6px' }}>
                <SearchIcon sx={{ fontSize: 17, color: 'custom.ink4' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          flex: '1 1 170px',
          minWidth: 150,
          maxWidth: 320,
          '& .MuiOutlinedInput-root': {
            height: 31,
            pl: '10px',
            borderRadius: '7px',
            bgcolor: 'custom.field',
            fontSize: '0.78rem',
            color: 'custom.ink',
            '& fieldset': { borderColor: 'custom.fieldLine' },
            '&:hover fieldset': { borderColor: 'custom.fieldLine' },
            '&.Mui-focused': { bgcolor: 'custom.surface' },
            '&.Mui-focused fieldset': { borderColor: 'custom.ink', borderWidth: '1px' },
          },
          '& .MuiOutlinedInput-input': { p: 0, height: '100%' },
        }}
      />

      <Select
        size="small"
        value={company}
        onChange={(event) => onCompanyChange(event.target.value)}
        IconComponent={UnfoldMoreIcon}
        slotProps={{ htmlInput: { 'aria-label': 'Tally company' } }}
        sx={{
          height: 31,
          maxWidth: 260,
          borderRadius: '7px',
          bgcolor: 'custom.field',
          fontSize: '0.78rem',
          fontWeight: 500,
          color: 'custom.ink',
          '& .MuiSelect-select': { py: 0, pl: '10px', pr: '26px !important' },
          '& fieldset': { borderColor: 'custom.fieldLine' },
          '&:hover fieldset': { borderColor: 'custom.fieldLine' },
          '&.Mui-focused fieldset': { borderColor: 'custom.ink', borderWidth: '1px' },
          '& .MuiSelect-icon': { fontSize: 16, right: 7, color: 'custom.ink3' },
        }}
      >
        {companies.map((name) => (
          <MenuItem key={name} value={name} sx={{ fontSize: '0.78rem' }}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  )
}
